import React, { useRef, useEffect, useState } from 'react'
import styled from 'styled-components'
import { UIElement } from '../../types/UIElements'
import { useContextMotifDetails } from '../../contexts/motifDetailsContext'
import { useContextUI } from '../../contexts/UIContext'
import routePaths from '../../constants/routePaths'
import { useLocation, useNavigate } from 'react-router-dom'
import { theme } from '../../styles/styled-components'
import { useTranslation } from 'react-i18next'
import { isMobile } from 'react-device-detect'
import type { DefaultTheme } from 'styled-components'
import { keywordTags } from '../../constants/tags'

const ITEM_PADDING = 24
const ITEM_PADDING_XL = 36
const FONT_SIZE = 13
const FONT_SIZE_XL = 19
const TRANSITION_DURATION = 500

const getItemPadding = (props: { theme: DefaultTheme }) =>
  window.matchMedia(`(min-width: ${props.theme.breakpoints.deviceW.xxl})`).matches
    ? ITEM_PADDING_XL
    : ITEM_PADDING

const Bar = styled.div`
  position: absolute;
  z-index: 4;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 50px;
  background: ${({ theme }) => theme.colors.black};
  color: #fff;
  border: #a5a5a5 1px solid;
  border-radius: 16px;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.06);
  white-space: nowrap;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: ${FONT_SIZE}px;

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.xxl}) {
    height: 70px;
    bottom: 6vh;
    font-size: ${FONT_SIZE_XL}px;
  }
`

const Label = styled.span`
  color: rgba(255, 255, 255, 0.65);
  padding: 0 ${ITEM_PADDING}px;
  line-height: 1;
  display: flex;
  align-items: center;
  min-width: 40px;
  width: fit-content;
  text-align: left;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  letter-spacing: 0.05em;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: ${FONT_SIZE}px;

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.xxl}) {
    padding: 0 ${ITEM_PADDING_XL}px;
    font-size: ${FONT_SIZE_XL}px;
  }
`

const Items = styled.div`
  display: flex;
  flex: 1;
  gap: 0;
  min-width: 0;
  height: 100%;
  border-radius: 16px;
  overflow: hidden;
`

const Separator = styled.span`
  display: block;
  position: absolute;
  left: 0;
  top: 12%;
  bottom: 12%;
  width: 1px;
  background: #a5a5a5;
  opacity: 0.7;
  z-index: 1;
`

const Item = styled.button<{ $active?: boolean; $width?: number; $open?: boolean }>`
  width: ${props => (props.$open ? props.$width ?? 'fit-content' : '0px')};
  min-width: 0;
  height: 100%;
  border: 0;
  will-change: width, padding, opacity, background, color;
  transition-property: width, padding, opacity, background, color;
  transition-duration: ${TRANSITION_DURATION}ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  outline: none;
  opacity: ${props => (props.$open ? 1 : 0)};
  padding: 0 ${props => (props.$open ? `${getItemPadding(props)}px` : '0px')};
  -webkit-tap-highlight-color: transparent;
  appearance: none;
  background: ${({ $active }) => ($active ? theme.colors.red : 'transparent')};
  color: #fff;
  line-height: 1;
  cursor: pointer;
  height: 100%;
  letter-spacing: 0.05em;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  text-overflow: ellipsis;
  font-family: ${({ theme }) => theme.font.regular};
  font-size: ${FONT_SIZE}px;
  text-transform: uppercase;

  &:hover {
    color: ${({ $active }) => ($active ? theme.colors.white : theme.colors.red)};
  }
  &:active {
    transform: translateY(1px);
  }
  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.xxl}) {
    font-size: ${FONT_SIZE_XL}px;
  }
`

const SelectedFilter = styled.button<{ $width: number; $open: boolean }>`
  width: ${props => props.$width}px;
  min-width: 0;
  height: 100%;
  border: 0;
  outline: none;
  background: ${props => (props.$width > 0 ? props.theme.colors.white : 'transparent')};
  color: ${props => (props.$width > 0 ? props.theme.colors.red : 'transparent')};
  will-change: width, padding, background, color;
  transition-property: width, padding, background, color;
  transition-duration: ${TRANSITION_DURATION}ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  line-height: 1;
  cursor: default;
  height: 100%;
  letter-spacing: 0.05em;
  position: relative;
  display: flex;
  text-align: left;
  align-items: center;
  justify-content: flex-start;
  overflow: hidden;
  text-overflow: ellipsis;
  padding: 0 ${props => (props.$width > 0 ? `${getItemPadding(props)}px` : '0')};
  font-family: ${({ theme }) => theme.font.regular};
  font-size: ${FONT_SIZE}px;

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.xxl}) {
    font-size: ${FONT_SIZE_XL}px;
  }
`

const CloseIconStyled = styled.img`
  width: ${FONT_SIZE}px;
  height: ${FONT_SIZE}px;
  display: block;

  @media (min-width: ${({ theme }) => theme.breakpoints.deviceW.xxl}) {
    width: ${FONT_SIZE_XL}px;
    height: ${FONT_SIZE_XL}px;
  }
`

const Close = styled(Item)`
  background: transparent;
  min-width: 0;
  width: ${props => (props.$open ? 'fit-content' : '0px')};
  padding: 0 ${props => (props.$open ? `${getItemPadding(props)}px` : '0px')};
  opacity: ${props => (props.$open ? 0.9 : 0)};
  will-change: width, padding, opacity, background, color;
  transition-property: width, padding, opacity, background, color;
  transition-duration: ${TRANSITION_DURATION}ms;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: ${props => (props.$open ? 'auto' : 'none')};

  &:disabled {
    opacity: 0 !important;
    cursor: default;
    background: transparent;
  }
`

interface Props {
  onClose?: () => void
}

const FilterBar: React.FC<Props> = ({ onClose }) => {
  const { t, i18n } = useTranslation()

  const location = useLocation()
  const navigate = useNavigate()
  const { elementsOpen, setElementsOpen, filterOutUiElement } = useContextUI()
  const {
    areDetailsOpen,
    setDetailsOpen,
    tagFilter,
    keywordFilter,
    geoFilter,
    applyGeoFilter,
    applyTagFilter,
    applyKeywordFilter,
  } = useContextMotifDetails()

  const [selectedWidth, setSelectedWidth] = useState(0)
  const [showCloseButton, setShowCloseButton] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const selectedRef = useRef<HTMLSpanElement>(null)
  const isOpen = !!(tagFilter || geoFilter || keywordFilter !== undefined)

  // Routes where FilterBar should be hidden
  const hiddenOnRoutes = [routePaths.GENERATE || '/generate']

  const pathname = location.pathname

  useEffect(() => {
    const isXXL = window.matchMedia(`(min-width: ${theme.breakpoints.deviceW.xxl})`).matches
    const padding = isXXL ? ITEM_PADDING_XL : ITEM_PADDING
    setSelectedWidth(
      isOpen && selectedRef.current ? selectedRef.current.offsetWidth + padding * 2 : 0,
    )
  }, [tagFilter, geoFilter, keywordFilter, isOpen])

  useEffect(() => {
    if (isOpen) {
      setShowCloseButton(true)
      setIsClosing(false)
    } else {
      setIsClosing(true)
      const timer = setTimeout(() => {
        setShowCloseButton(false)
        setIsClosing(false)
      }, TRANSITION_DURATION * 2) // delay + transition
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  if (
    elementsOpen.includes(UIElement.WELCOME) ||
    hiddenOnRoutes.some(r => r && pathname.startsWith(r))
  ) {
    return null
  }

  const toggleTagFilter = () => {
    uiConditionsForFilters()
    if (tagFilter) {
      applyTagFilter(undefined)
      window.history.replaceState(null, '', '/')
      setElementsOpen([UIElement.CONTROLS])
    } else {
      const opening = !elementsOpen.includes(UIElement.TAGFILTER)
      window.history.replaceState(null, '', opening ? routePaths.TAGS : '/')
      setElementsOpen(opening ? [UIElement.TAGFILTER] : [UIElement.CONTROLS])
    }
  }

  const toggleKeywordFilter = () => {
    uiConditionsForFilters()
    if (keywordFilter !== undefined) {
      applyKeywordFilter(undefined)
      window.history.replaceState(null, '', '/')
      setElementsOpen([UIElement.CONTROLS])
    } else {
      const opening = !elementsOpen.includes(UIElement.KEYWORDFILTER)
      window.history.replaceState(null, '', opening ? routePaths.KEYWORDS : '/')
      setElementsOpen(opening ? [UIElement.KEYWORDFILTER] : [UIElement.CONTROLS])
    }
  }

  const uiConditionsForFilters = () => {
    if (location.pathname !== routePaths.HOME) navigate(routePaths.HOME)
    if (areDetailsOpen) setDetailsOpen(false)
    if (elementsOpen.includes(UIElement.GALLERY)) filterOutUiElement(UIElement.GALLERY)
    if (elementsOpen.includes(UIElement.ABOUT)) filterOutUiElement(UIElement.ABOUT)
  }

  const toggleGeoFilter = () => {
    uiConditionsForFilters()
    if (geoFilter) {
      applyGeoFilter(undefined)
      window.history.replaceState(null, '', '/')
      setElementsOpen([UIElement.CONTROLS])
    } else {
      const opening = !elementsOpen.includes(UIElement.GEOFILTER)
      window.history.replaceState(null, '', opening ? routePaths.LOCATION : '/')
      setElementsOpen(opening ? [UIElement.GEOFILTER] : [UIElement.CONTROLS])
    }
  }

  const clearFilters = () => {
    setDetailsOpen(false)
    clearFilterBar(setElementsOpen, applyTagFilter, applyGeoFilter, applyKeywordFilter)
  }

  if (isMobile && window.innerHeight < 600) return null

  // Determine which filter is open or active
  const isThemeFilterOpen =
    elementsOpen.includes(UIElement.TAGFILTER) ||
    (!!tagFilter && !geoFilter && keywordFilter === undefined)
  const isGeoFilterOpen =
    elementsOpen.includes(UIElement.GEOFILTER) ||
    (!!geoFilter && !tagFilter && keywordFilter === undefined)
  const isKeywordFilterOpen =
    elementsOpen.includes(UIElement.KEYWORDFILTER) ||
    (keywordFilter !== undefined && !tagFilter && !geoFilter)

  const handleTagFilterClick = () => {
    if (isThemeFilterOpen && tagFilter) {
      applyTagFilter(undefined)
      setElementsOpen([UIElement.TAGFILTER])
      setDetailsOpen(false)
      return
    }
    if (isThemeFilterOpen && !tagFilter) return
    setDetailsOpen(false)
    toggleTagFilter()
    onClose?.()
  }

  const handleKeywordFilterClick = () => {
    if (isKeywordFilterOpen && keywordFilter !== undefined) {
      applyKeywordFilter(undefined)
      setElementsOpen([UIElement.KEYWORDFILTER])
      setDetailsOpen(false)
      return
    }
    if (isKeywordFilterOpen && keywordFilter === undefined) {
      window.history.replaceState(null, '', '/')
      setElementsOpen([UIElement.CONTROLS])
      setDetailsOpen(false)
      return
    }
    setDetailsOpen(false)
    toggleKeywordFilter()
    onClose?.()
  }

  const handleGeoFilterClick = () => {
    if (isGeoFilterOpen && geoFilter) {
      applyGeoFilter(undefined)
      setElementsOpen([UIElement.GEOFILTER])
      setDetailsOpen(false)
      return
    }
    if (isGeoFilterOpen && !geoFilter) return
    setDetailsOpen(false)
    toggleGeoFilter()
    onClose?.()
  }

  const selectedKeyword =
    keywordFilter !== undefined
      ? keywordTags.find(keyword => keyword.id === keywordFilter)
      : undefined
  const selectedKeywordName = selectedKeyword
    ? `${i18n.language.startsWith('en') ? selectedKeyword.EN_Name : selectedKeyword.Name} (${
        selectedKeyword.count
      })`
    : ''

  return (
    <Bar role="toolbar" aria-label="Filters">
      <Items>
        <Label>{t('filterBar.filteredBy')}</Label>
        <Item
          aria-label="Geographic search"
          $active={isGeoFilterOpen}
          $open={!isOpen || isGeoFilterOpen}
          onClick={handleGeoFilterClick}
        >
          {!isThemeFilterOpen && <Separator />}
          {t('filterBar.location')}
        </Item>
        <Item
          aria-label="Thematic search"
          $active={isThemeFilterOpen}
          $open={!isOpen || isThemeFilterOpen}
          onClick={handleTagFilterClick}
        >
          {!isGeoFilterOpen && <Separator />}
          {t('filterBar.theme')}
        </Item>
        {/* <Item
          aria-label="Keyword search"
          $active={isKeywordFilterOpen}
          $open={!isOpen || isKeywordFilterOpen}
          onClick={handleKeywordFilterClick}
        >
          {!isGeoFilterOpen && <Separator />}
          {t('filterBar.keyword')}
        </Item> */}
        <SelectedFilter
          aria-label="Selected filter"
          $width={selectedWidth}
          $open={isOpen}
          className="SelectedFilter"
          tabIndex={-1}
          disabled
        >
          {isOpen && (
            <span
              ref={selectedRef}
              style={{
                display: 'inline-block',
                whiteSpace: 'nowrap',
                textTransform: 'uppercase',
              }}
            >
              {keywordFilter !== undefined
                ? selectedKeywordName
                : t(`${tagFilter ? 'tagFilter' : 'geoFilter'}.${tagFilter || geoFilter}`)}
            </span>
          )}
        </SelectedFilter>
        {showCloseButton && (
          <Close
            aria-label="Close filters"
            $open={!isClosing}
            onClick={() => {
              clearFilters()
            }}
          >
            <CloseIconStyled src="/icons/filterbar-close.svg" alt="close" />
          </Close>
        )}
      </Items>
    </Bar>
  )
}

export default FilterBar

// Export a function to clear filters and reset FilterBar state
export function clearFilterBar(
  setElementsOpen: (v: UIElement[]) => void,
  applyTagFilter: (v: undefined) => void,
  applyGeoFilter: (v: undefined) => void,
  applyKeywordFilter: (v: undefined) => void,
) {
  applyTagFilter(undefined)
  applyGeoFilter(undefined)
  applyKeywordFilter(undefined)
  setElementsOpen([UIElement.CONTROLS])
}
